import React, { useState } from 'react'
import { TextField, FormGroup, FormControlLabel, Switch, Stack, FormControl, InputLabel, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MultiLangDialogAndSingleDisplayIpt from './MultiLangDialogAndSingleDisplayIpt';
import { EXCLUDE_FOR_SIDEBAR_IPTS, KEYS_OF_FILTER_KEY_LIST, NODE_TYPES } from '../constant';
import useFilterKey from '../hooks/useFilterKey';


export default function SbcCommon({
    data,
    handleIptChange,
    handleMultiIptChange,
    handleSwitchChange,
    handleSelectChange
}) {

    const { filterKeyList, setStorageItem } = useFilterKey(KEYS_OF_FILTER_KEY_LIST)
    const [customSequence, setCustomSequence] = useState(false)

    const keysToExclude = EXCLUDE_FOR_SIDEBAR_IPTS?.[data?.type] || []
    const hasValidationFields = !keysToExclude.includes('inputMode') || !keysToExclude.includes('inputRegex') ||
        !keysToExclude.includes('minSize') || !keysToExclude.includes('maxSize')

    return (
        <Stack direction={'column'} spacing={1}>
            {/* General Section */}
            <Accordion defaultExpanded className="sidebar-section">
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className="sidebar-section-summary">
                    <Typography variant="caption">General</Typography>
                </AccordionSummary>
                <AccordionDetails className="sidebar-section-details">
                    <Stack spacing={1.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="type-label">Type</InputLabel>
                            <Select
                                variant='filled'
                                labelId="type-label"
                                id="type"
                                label="Type"
                                value={data?.type || ''}
                                color='success'
                                name='type'
                                onChange={handleSelectChange}
                            >
                                {Object.keys(NODE_TYPES).map(key => <MenuItem key={key} value={NODE_TYPES[key]}>{NODE_TYPES[key]}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {customSequence ? (
                            <TextField
                                variant='filled'
                                type='number'
                                color='success'
                                label='Sequence'
                                name='sequence'
                                size="small"
                                value={data?.sequence || ''}
                                onChange={handleIptChange}
                                helperText={
                                    <span style={{ cursor: 'pointer', color: '#4A90D9', fontSize: 10 }} onClick={() => setCustomSequence(false)}>
                                        ← back to presets
                                    </span>
                                }
                            />
                        ) : (
                            <FormControl fullWidth size="small">
                                <InputLabel id="sequence-label">Sequence</InputLabel>
                                <Select
                                    variant='filled'
                                    labelId="sequence-label"
                                    id="sequence"
                                    label="Sequence"
                                    value={data?.sequence || 10}
                                    color='success'
                                    name='sequence'
                                    onChange={handleSelectChange}
                                >
                                    {(() => {
                                        const current = data?.sequence
                                        const presets = [
                                            { n: 10,  label: '1st — Beginning' },
                                            { n: 20,  label: '2nd' },
                                            { n: 30,  label: '3rd' },
                                            { n: 40,  label: '4th' },
                                            { n: 50,  label: '5th' },
                                            { n: 60,  label: '6th' },
                                            { n: 70,  label: '7th' },
                                            { n: 80,  label: '8th' },
                                            { n: 90,  label: '9th' },
                                            { n: 100, label: '10th' },
                                            { n: 110, label: '11th' },
                                            { n: 120, label: '12th' },
                                            { n: 130, label: '13th' },
                                            { n: 140, label: '14th' },
                                            { n: 150, label: '15th — End' },
                                        ]
                                        if (current && !presets.some(p => p.n === current)) {
                                            const lastPreset = presets[presets.length - 1]
                                            const suffix = current < 10 ? ' (Before all)'
                                                : current > lastPreset.n ? ` (After ${lastPreset.label.split(' —')[0]})`
                                                : ''
                                            presets.push({ n: current, label: `${current}${suffix}` })
                                        }
                                        return presets.map(({ n, label }) => (
                                            <MenuItem key={n} value={n}>{label}</MenuItem>
                                        ))
                                    })()}
                                </Select>
                                <Typography
                                    variant="caption"
                                    onClick={() => setCustomSequence(true)}
                                    sx={{ cursor: 'pointer', color: '#4A90D9', fontSize: 10, mt: 0.25 }}
                                >
                                    Custom value…
                                </Typography>
                            </FormControl>
                        )}
                        <TextField
                            variant='filled'
                            type='text'
                            color='success'
                            label='Filter Key'
                            name='filterKey'
                            size="small"
                            value={data?.filterKey || ''}
                            inputProps={{ list: "itemList" }}
                            onChange={handleIptChange}
                            onBlur={e => setStorageItem(e.target.value)}
                        />
                        <datalist id="itemList">
                            {filterKeyList?.map(i => <option key={i?.value}>{i?.value}</option>)}
                        </datalist>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* Content Section */}
            <Accordion defaultExpanded className="sidebar-section">
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className="sidebar-section-summary">
                    <Typography variant="caption">Content</Typography>
                </AccordionSummary>
                <AccordionDetails className="sidebar-section-details">
                    <Stack spacing={1.5}>
                        <MultiLangDialogAndSingleDisplayIpt
                            keyName={'question'}
                            valueObj={data?.question}
                            handleMultiIptChange={handleMultiIptChange}
                        />
                        <MultiLangDialogAndSingleDisplayIpt
                            keyName={'answerLabel'}
                            valueObj={data?.answerLabel}
                            handleMultiIptChange={handleMultiIptChange}
                        />
                        <FormGroup>
                            <FormControlLabel
                                label="Required"
                                control={<Switch name='mandatory' checked={data?.mandatory} onChange={handleSwitchChange} />}
                                color='success'
                            />
                        </FormGroup>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {/* Validation Section — only if the type has validation fields */}
            {hasValidationFields && (
                <Accordion defaultExpanded className="sidebar-section">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} className="sidebar-section-summary">
                        <Typography variant="caption">Validation</Typography>
                    </AccordionSummary>
                    <AccordionDetails className="sidebar-section-details">
                        <Stack spacing={1.5}>
                            {!keysToExclude.includes('inputMode') && (
                                <FormControl fullWidth size="small">
                                    <InputLabel id="select-label">Input Mode</InputLabel>
                                    <Select
                                        variant='filled'
                                        labelId="select-label"
                                        id="select"
                                        label="Input Mode"
                                        value={data?.inputMode || ''}
                                        color='success'
                                        name='inputMode'
                                        onChange={handleSelectChange}
                                    >
                                        <MenuItem value={'normal'}>Normal</MenuItem>
                                        <MenuItem value={'number'}>Number</MenuItem>
                                    </Select>
                                </FormControl>
                            )}
                            {!keysToExclude.includes('inputRegex') && (
                                <TextField
                                    variant='filled'
                                    type='text'
                                    color='success'
                                    label='Input RegEx'
                                    name='inputRegex'
                                    size="small"
                                    value={data?.inputRegex || ''}
                                    onChange={handleIptChange}
                                />
                            )}
                            {!keysToExclude.includes('minSize') && (
                                <TextField
                                    variant='filled'
                                    type='number'
                                    color='success'
                                    label='Min Character Length'
                                    name='minSize'
                                    size="small"
                                    value={data?.minSize || ''}
                                    onChange={handleIptChange}
                                />
                            )}
                            {!keysToExclude.includes('maxSize') && (
                                <TextField
                                    variant='filled'
                                    type='number'
                                    color='success'
                                    label='Max Character Length'
                                    name='maxSize'
                                    size="small"
                                    value={data?.maxSize || ''}
                                    onChange={handleIptChange}
                                />
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}
        </Stack>
    )
}
